import { Component, inject, OnInit, ViewChild, AfterViewInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, NgZone, ElementRef, effect, signal, computed } from '@angular/core';


import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TauriService } from '../../../shared/services/tauri.service';
import { TimeEntriesService } from '../../../shared/services/time-entries.service';
import { ActivityLogComponent } from './activity-log/activity-log';
import { TimeGridComponent } from './time-grid/time-grid';
import { TimelineTask, TimeEntry, TimeSlot } from '../../../shared/models/activity.model';
import { getCurrentUser } from 'aws-amplify/auth';
import { Subscription, filter, take } from 'rxjs';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { PreventScrollPropagationDirective } from '../../../shared/directives/prevent-scroll-propagation.directive';
import { GoogleCalendarService, GoogleCalendarEvent } from '../../../shared/services/google-calendar.service';
import { UserService } from '../../../shared/services/user.service';
import { UserSettings } from '../../../shared/models/user.model';


// component to display activity from memetime and time entry
@Component({
  selector: 'app-task-timeline',
  imports: [CommonModule, FormsModule, ActivityLogComponent, TimeGridComponent, DateInputsModule, PreventScrollPropagationDirective],
  templateUrl: './task-timeline.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskTimelineComponent implements OnInit, AfterViewInit, OnDestroy {
  private tauriService = inject(TauriService);
  private timeEntriesService = inject(TimeEntriesService);
  readonly googleCalendar = inject(GoogleCalendarService);
  private userService = inject(UserService);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  constructor() {
    // React to demo mode / real connection being toggled from the Settings page
    effect(() => {
      const isConnected = this.googleCalendar.connected();
      // selectedDate may not be set yet on first run — guard against that
      if (this.selectedDate) {
        this.loadCalendarEvents();
      }
    });
  }

  @ViewChild(ActivityLogComponent) activityLogComponent!: ActivityLogComponent;
  @ViewChild(TimeGridComponent) timeGridComponent!: TimeGridComponent;

  private allTimelineTasks: TimelineTask[] = [];
  private subscriptions: Subscription[] = [];

  timelineTasks: TimelineTask[] = [];
  timeEntries: TimeEntry[] = [];
  timeSlots: TimeSlot[] = [];
  calendarEvents = signal<GoogleCalendarEvent[]>([]);
  calendarLoading = signal(false);
  showCalendarEvents = true;

  /** Derived map of calendarId → hex color, updated reactively from calendarList signal */
  calendarColorMap = computed(() => {
    const map = new Map<string, string>();
    for (const cal of this.googleCalendar.calendarList()) {
      map.set(cal.id, cal.backgroundColor);
    }
    return map;
  });

  currentUserId: string = '';



  zoomOptions = [1, 5, 10, 15, 30, 60];
  selectedZoom = 5;
  selectedDate: string = "";
  selectedDateValue: Date = new Date(); // For Kendo DatePicker
  rowHeight = 32;
  formattedDate: string = ''; // Pré-calculé au lieu de getFormattedDate()

  // Loading states
  isLoadingTasks = true;
  isLoadingEntries = true;
  isInitialLoad = true;
  private loadingStartTime = 0;
  private readonly MIN_LOADING_TIME = 300; // Minimum 300ms to prevent flashing
  private hasInitiatedTaskLoad = false;
  private hasInitiatedEntriesLoad = false;

  readonly START_HOUR = 6;
  readonly END_HOUR = 23;
  private isSyncingLeft = false;
  private isSyncingRight = false;

  leftWidth = Math.floor(window.innerWidth * 0.65);
  rightWidth = Math.floor(window.innerWidth * 0.35) - 24;
  private splitRestored = false;
  resizing = false;
  startX = 0;

  currentLineTop: number = -1;
  private clockInterval: any;

  hoveredSlotIndex: number | null = null;

  async ngOnInit() {
    console.log('🚀 [Timeline] ngOnInit started');

    // 1. Set up date FIRST. `?date=YYYY-MM-DD` wins over today, so coming back
    //    from encoding a slot on a past day returns to that day rather than now.
    const day = this.parseDateParam(this.route.snapshot.queryParamMap.get('date')) ?? new Date();
    day.setHours(0, 0, 0, 0);
    this.selectedDateValue = day;
    this.selectedDate = this.getLocalDateString(day);
    console.log('🟢 [Timeline] Selected date set to:', this.selectedDate);

    // 2. Initialize UI
    this.updateFormattedDate();
    this.updateRowHeight();
    this.generateSlots();

    // 2b. Restore split position from user settings (once, deferred to afterViewInit)
    this.subscriptions.push(
      this.userService.settings$.pipe(
        filter((s): s is UserSettings => s != null && s.activitySplitPosition != null && s.activitySplitPosition > 0),
        take(1)
      ).subscribe(settings => {
        this.applySplitRatio(settings.activitySplitPosition!);
      })
    );

    // 3. Set up loading state
    this.loadingStartTime = Date.now();
    this.hasInitiatedTaskLoad = false; // Will be set to true AFTER we request data
    this.hasInitiatedEntriesLoad = false;

    // 4. Set up subscriptions FIRST - before any async operations
    // Use a flag to track if we've explicitly requested data
    this.subscriptions.push(
      this.tauriService.timelineTasks$.subscribe(tasks => {
        console.log('📥 [Timeline] Received tasks:', tasks.length, 'hasInitiatedTaskLoad:', this.hasInitiatedTaskLoad);

        // Only process if we've explicitly requested data for this date
        if (this.hasInitiatedTaskLoad) {
          this.allTimelineTasks = tasks;
          this.filterActivityData();

          const elapsed = Date.now() - this.loadingStartTime;
          const delay = Math.max(0, this.MIN_LOADING_TIME - elapsed);
          setTimeout(() => {
            this.isLoadingTasks = false;
            this.isInitialLoad = false;
            console.log('✅ [Timeline] Tasks loading complete, filtered:', this.timelineTasks.length);
            this.cdr.markForCheck();
          }, delay);

          this.cdr.markForCheck();
        } else {
          console.log('⏭️ [Timeline] Ignoring tasks - not yet requested data');
        }
      })
    );

    this.subscriptions.push(
      this.timeEntriesService.timeEntries$.subscribe(entries => {
        console.log('📥 [Timeline] Received entries:', entries.length, 'hasInitiatedEntriesLoad:', this.hasInitiatedEntriesLoad);

        // Only process if we've explicitly requested data
        if (this.hasInitiatedEntriesLoad) {
          this.timeEntries = entries;

          const elapsed = Date.now() - this.loadingStartTime;
          const delay = Math.max(0, this.MIN_LOADING_TIME - elapsed);
          setTimeout(() => {
            this.isLoadingEntries = false;
            console.log('✅ [Timeline] Entries loading complete, count:', this.timeEntries.length);
            this.cdr.markForCheck();
          }, delay);

          this.cdr.markForCheck();
        } else {
          console.log('⏭️ [Timeline] Ignoring entries - not yet requested data');
        }
      })
    );

    // 5. Get user info (can happen in parallel with data load)
    this.loadUserInfo();

    // 6. NOW set the flags and load data - this ensures the subscription processes it
    // Use queueMicrotask to ensure the initial BehaviorSubject emission (hasInitiatedTaskLoad=false)
    // is fully processed before we set the flag and request new data
    queueMicrotask(() => {
      console.log('🚀 [Timeline] Setting flags and loading data for:', this.selectedDate);
      this.hasInitiatedTaskLoad = true;
      this.hasInitiatedEntriesLoad = true;
      this.loadingStartTime = Date.now(); // Reset timer for actual load

      this.loadActivityForSelectedDate();
      this.loadDayData();
      this.loadCalendarEvents();
    });


    // 7. Set up clock
    this.calculateCurrentTimePosition();
    this.clockInterval = setInterval(() => {
      this.calculateCurrentTimePosition();
      this.cdr.markForCheck();
    }, 60000);

    console.log('✅ [Timeline] ngOnInit completed');
  }

  private async loadUserInfo() {
    try {
      const user = await getCurrentUser();
      this.currentUserId = user.userId;
      console.log('✅ [Timeline] User loaded:', this.currentUserId);
    } catch (e) {
      console.error("❌ [Timeline] User not logged in", e);
    }
  }

  ngAfterViewInit() {
    const leftEl = this.activityLogComponent.scrollContainer.nativeElement;
    const rightEl = this.timeGridComponent.scrollContainer.nativeElement;
    leftEl.addEventListener('scroll', () => {
      if (!this.isSyncingLeft) {
        this.isSyncingRight = true;
        rightEl.scrollTop = leftEl.scrollTop;
      }
      this.isSyncingLeft = false;
    });
    rightEl.addEventListener('scroll', () => {
      if (!this.isSyncingRight) {
        this.isSyncingLeft = true;
        leftEl.scrollTop = rightEl.scrollTop;
      }
      this.isSyncingRight = false;
    });
    setTimeout(() => this.scrollToNow(), 100);
  }

  ngOnDestroy() {
    if (this.clockInterval) clearInterval(this.clockInterval);
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  onConfigChange() {
    console.log('🔄 [Timeline] onConfigChange triggered for date:', this.selectedDate);
    this.loadingStartTime = Date.now();
    this.isLoadingTasks = true;
    this.isLoadingEntries = true;
    this.hasInitiatedTaskLoad = true;
    this.hasInitiatedEntriesLoad = true;

    this.updateRowHeight();
    this.updateFormattedDate();
    this.generateSlots();
    // Don't call filterActivityData() here - it will be called by the subscription when data arrives
    this.loadDayData();
    this.loadActivityForSelectedDate();
    this.loadCalendarEvents();
    this.calculateCurrentTimePosition();
    this.syncDateToUrl();

    this.cdr.markForCheck();
  }

  /**
   * Mirror the viewed day into `?date=`, so a reload — or a round trip through the
   * time-entry page — comes back to the day being looked at, not to today.
   */
  private syncDateToUrl() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { date: this.selectedDate },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  async loadActivityForSelectedDate() {
    console.log('🔄 [Timeline] loadActivityForSelectedDate called for:', this.selectedDate);
    // Parse as local time to avoid timezone issues
    const date = new Date(this.selectedDate + 'T00:00:00');
    console.log('🔄 [Timeline] Calling tauriService.getTasksForDate with:', date.toISOString());
    try {
      await this.tauriService.getTasksForDate(date);
      console.log('✅ [Timeline] loadActivityForSelectedDate completed successfully');
    } catch (err) {
      console.error('❌ [Timeline] loadActivityForSelectedDate failed:', err);
    }
  }

  async loadDayData() {
    console.log('🔄 [Timeline] loadDayData called for:', this.selectedDate);
    await this.timeEntriesService.loadEntriesForDate(this.selectedDate);
    console.log('✅ [Timeline] loadDayData completed');
  }

  async loadCalendarEvents() {
    if (!this.googleCalendar.connected()) {
      this.calendarEvents.set([]);
      this.calendarLoading.set(false);
      return;
    }
    this.calendarLoading.set(true);
    try {
      const start = new Date(this.selectedDate + 'T00:00:00');
      const end = new Date(this.selectedDate + 'T23:59:59');
      const events = await this.googleCalendar.getEvents(start, end);
      this.calendarEvents.set(events);
    } finally {
      this.calendarLoading.set(false);
      this.cdr.markForCheck();
    }
  }

  filterActivityData() {
    if (!this.selectedDate) {
      console.log('⚠️ [Timeline] filterActivityData: no selectedDate');
      return;
    }

    const startOfDay = new Date(this.selectedDate + 'T00:00:00');
    const endOfDay = new Date(this.selectedDate + 'T23:59:59');

    console.log('🔍 [Timeline] Filtering', this.allTimelineTasks.length, 'tasks for date:', this.selectedDate);
    console.log('🔍 [Timeline] Date range:', startOfDay.toISOString(), 'to', endOfDay.toISOString());

    if (this.allTimelineTasks.length > 0) {
      console.log('🔍 [Timeline] First task startTime:', this.allTimelineTasks[0].startTime);
    }

    this.timelineTasks = this.allTimelineTasks.filter(task => {
      const taskTime = new Date(task.startTime);
      const matches = taskTime >= startOfDay && taskTime <= endOfDay;
      if (!matches && this.allTimelineTasks.length <= 5) {
        console.log('🔍 [Timeline] Task excluded:', task.name, 'time:', taskTime.toISOString());
      }
      return matches;
    });

    console.log('✅ [Timeline] Filtered to', this.timelineTasks.length, 'tasks for selected day');
    this.cdr.markForCheck();
  }

  calculateCurrentTimePosition() {
    const now = new Date();
    const todayStr = this.getLocalDateString(now);
    if (this.selectedDate !== todayStr) {
      this.currentLineTop = -1;
      return;
    }
    const currentHour = now.getHours();
    const currentMin = now.getMinutes();
    if (currentHour < this.START_HOUR || currentHour > this.END_HOUR) {
      this.currentLineTop = -1;
      return;
    }
    const minutesSinceStart = ((currentHour - this.START_HOUR) * 60) + currentMin;
    const pixels = (minutesSinceStart / this.selectedZoom) * this.rowHeight;
    this.currentLineTop = pixels;
  }

  scrollToNow() {
    if (this.currentLineTop > 0 && this.activityLogComponent?.scrollContainer) {
      const container = this.activityLogComponent.scrollContainer.nativeElement;
      const containerHeight = container.clientHeight;
      const targetScroll = this.currentLineTop - (containerHeight / 2);
      container.scrollTop = targetScroll;
    }
  }

  updateRowHeight() {
    switch (this.selectedZoom) {
      case 1: this.rowHeight = 24; break;
      case 5: this.rowHeight = 32; break;
      case 10: this.rowHeight = 48; break;
      case 15: this.rowHeight = 60; break;
      case 30: this.rowHeight = 90; break;
      case 60: this.rowHeight = 120; break;
      default: this.rowHeight = 32; break;
    }
  }

  generateSlots() {
    this.timeSlots = [];
    // Parse as local time to avoid timezone issues
    const date = new Date(this.selectedDate + 'T00:00:00');
    date.setHours(this.START_HOUR, 0, 0, 0);
    const end = new Date(this.selectedDate + 'T00:00:00');
    end.setHours(this.END_HOUR, 0, 0, 0);
    let index = 0;
    while (date < end) {
      const minutes = date.getMinutes();
      const isHour = date.getMinutes() === 0;
      let label = '';
      if (isHour) {
        label = `${date.getHours()}:00`;
      } else {
        if (this.selectedZoom <= 30 && minutes === 30) label = `${date.getHours()}:30`;
        else if (this.selectedZoom <= 15 && (minutes === 15 || minutes === 45)) label = `${date.getHours()}:${minutes}`;
      }
      this.timeSlots.push({
        index: index++,
        time: new Date(date),
        label: label,
        isHourMark: isHour
      });
      date.setMinutes(date.getMinutes() + this.selectedZoom);
    }
  }

  onSlotHover(index: number | null) {
    this.hoveredSlotIndex = index;
    this.cdr.markForCheck();
  }

  /**
   * Time entry creation is a full page now. The selected slot travels in the URL,
   * and the page loads the Tauri activities for that day itself — which is why the
   * date-reload dance that used to guard the modal is gone from here.
   */
  handleSelection(event: { startTime: Date; endTime: Date }) {
    this.router.navigate(['/activity/time-entries/new'], {
      queryParams: {
        start: event.startTime.toISOString(),
        end: event.endTime.toISOString(),
      },
    });
  }


  // Handle Kendo DatePicker change
  onDatePickerChange(value: Date | null) {
    if (value) {
      this.selectedDateValue = value;
      this.selectedDate = this.getLocalDateString(value);
      this.onConfigChange();
    }
  }

  /** `YYYY-MM-DD` as a local date, or null when absent or malformed. */
  private parseDateParam(raw: string | null): Date | null {
    if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
    const d = new Date(raw + 'T00:00:00');
    return isNaN(d.getTime()) ? null : d;
  }

  // Date navigation helpers
  navigateDay(offset: number) {
    // Parse the date string as local time (not UTC) by adding T00:00:00
    const current = new Date(this.selectedDate + 'T00:00:00');
    current.setDate(current.getDate() + offset);
    this.selectedDate = this.getLocalDateString(current);
    this.selectedDateValue = new Date(current); // Create new Date to avoid reference issues
    this.selectedDateValue.setHours(0, 0, 0, 0);
    this.onConfigChange();
  }

  goToToday() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.selectedDateValue = today;
    this.selectedDate = this.getLocalDateString(today);
    this.onConfigChange();
    setTimeout(() => this.scrollToNow(), 100);
  }

  updateFormattedDate(): void {
    // Parse the date string as local time (not UTC)
    const date = new Date(this.selectedDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      this.formattedDate = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      this.formattedDate = 'Yesterday';
    } else {
      this.formattedDate = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
  }

  startResize(event: MouseEvent) {
    event.preventDefault();
    this.resizing = true;
    this.startX = event.clientX;

    // Run outside Angular zone to avoid excessive change detection during resize
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.onResize);
      document.addEventListener('mouseup', this.stopResize);
    });
  }

  onResize = (event: MouseEvent) => {
    if (!this.resizing) return;
    const dx = event.clientX - this.startX;
    this.startX = event.clientX;
    this.leftWidth += dx;
    if (this.leftWidth < 200) this.leftWidth = 200;
    if (this.leftWidth > window.innerWidth - 200) this.leftWidth = window.innerWidth - 200;
    this.rightWidth = window.innerWidth - this.leftWidth - 24;
    this.cdr.detectChanges(); // Manual change detection
  };

  stopResize = () => {
    this.resizing = false;
    document.removeEventListener('mousemove', this.onResize);
    document.removeEventListener('mouseup', this.stopResize);
    this.ngZone.run(() => {
      this.cdr.markForCheck();
      // Save split position as percentage of total width
      const totalWidth = this.leftWidth + this.rightWidth + 24;
      const pct = Math.round((this.leftWidth / totalWidth) * 100);
      this.userService.updateUserSettings({ activitySplitPosition: pct });
    });
  };

  /** Apply a saved percentage to the current container width */
  private applySplitRatio(pct: number) {
    const totalWidth = this.leftWidth + this.rightWidth + 24;
    this.leftWidth = Math.floor(totalWidth * pct / 100);
    if (this.leftWidth < 200) this.leftWidth = 200;
    if (this.leftWidth > totalWidth - 200) this.leftWidth = totalWidth - 200;
    this.rightWidth = totalWidth - this.leftWidth - 24;
    this.splitRestored = true;
    this.cdr.markForCheck();
  }

  // Helper to get date string in local timezone (YYYY-MM-DD format)
  private getLocalDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
